import { Atom, atom } from 'jotai'
import { orderBy } from 'lodash'
import { alphamapClient } from 'src/clients'
import {
  AMOwnershipEntityPreviewResponse,
  AMOwnershipSearchEntitiesResponse,
  AMOwnershipParcelComplete,
  AMOwnershipEntityOverviewResponse,
  AMOwnershipGetParcelByIdResponse,
  Point,
  Feature,
  Polygon,
  MultiPolygon,
} from 'src/common-services'
import { ifNotServerError } from 'src/utils/ajax'
import { atomFamily } from 'src/utils/jotai'

import * as coreAtoms from 'src/v2/state/core'
import * as atoms from 'src/v2/state/owner-reveal'
import * as searchAtoms from 'src/v2/state/map-explorer/search'
import { SelectedEntity } from 'src/v2/types/core'

export const searchQuery = atom<string>('')
// Name + Address for entities, Address for parcels
export const searchNameQuery = atom<string>('')
export const searchAddressQuery = atom<string>('')

export type SearchQueryType = 'PLACE' | 'PERSONS' | 'ORGANIZATIONS'
export const searchQueryType = atom<SearchQueryType>('PLACE')

export type SearchQueryEntityType = 'PERSONS' | 'ORGANIZATIONS'
export const searchQueryEntityType = atom<SearchQueryEntityType>('PERSONS')

export const resultsTotal = atom<number>(0)
export const resultsPage = atom<number>(1)

export const entitiesResults = atom<AMOwnershipSearchEntitiesResponse | undefined>(undefined)
export const entitiesSortFilter = atom<'a-z' | 'z-a' | 'best-match'>('best-match')
export const entitiesResultForUI = atom<AMOwnershipSearchEntitiesResponse | undefined>((get) => {
  const entities = get(entitiesResults)
  const sortFilter = get(entitiesSortFilter)

  if (!entities) {
    return undefined
  }

  if (sortFilter === 'best-match') {
    return entities
  }

  const sortedEntities = orderBy(entities, 'name', sortFilter === 'a-z' ? 'asc' : 'desc')
  return sortedEntities
})

export const selectedEntity = atom(async (get) => {
  const id = await get(atoms.selectedEntityId)
  if (!id) {
    return undefined
  }
  return await ifNotServerError(get(entityPreview(id)), undefined)
})

export const entityPreview = atomFamily<string, Atom<Promise<AMOwnershipEntityPreviewResponse | undefined>>>((id) =>
  atom(async () => await alphamapClient.ownership.getEntityPreview(id))
)
export const entityOverview = atom<Promise<AMOwnershipEntityOverviewResponse | undefined>>(async (get) => {
  const id = await get(atoms.selectedEntityId)
  if (!id) {
    return undefined
  }

  return await alphamapClient.ownership.getEntityOverview(id)
})

export const parcelsResults = atom<AMOwnershipParcelComplete[] | undefined>(undefined)

export const parcelPreview = atomFamily<string, Atom<Promise<AMOwnershipGetParcelByIdResponse | undefined>>>((id) =>
  atom(async () => await alphamapClient.ownership.getParcelPreviewById(id))
)

export const selectedParcel = atom<Promise<AMOwnershipGetParcelByIdResponse | undefined> | undefined>(async (get) => {
  const id = await get(atoms.selectedParcelId)
  if (!id) {
    return undefined
  }
  return await ifNotServerError(get(parcelPreview(id)), undefined)
})

export const parcelPoints = atom<Promise<Feature<Point>[]>>(async (get) => {
  const entity = await get(entityOverview)
  if (entity !== undefined) {
    return entity.parcelPoints
  }

  const parcels = get(parcelsResults)
  if (parcels !== undefined && parcels.length > 0) {
    return parcels.map((parcel) => parcel.point)
  }

  return []
})

export const parcelPolygons = atom<Promise<Feature<Polygon | MultiPolygon>[]>>(async (get) => {
  const entity = await get(entityOverview)
  if (entity !== undefined) {
    return entity.parcelPolygons
  }
  return []
})

export type SidebarState = 'EMPTY' | 'OWNER_RESULTS' | 'PARCEL_RESULTS' | SelectedEntity['type']
export const sidebarState = atom<Promise<SidebarState>>(async (get) => {
  const entities = get(entitiesResults)
  const searchResults = await get(searchAtoms.searchResults)
  const _selectedEntity = await get(coreAtoms.selectedEntity)

  if (_selectedEntity) {
    return _selectedEntity.type
  }

  if (entities) {
    return 'OWNER_RESULTS'
  }
  if (!!searchResults) {
    return 'PARCEL_RESULTS'
  }

  return 'EMPTY'
})

export const sidebarContentLoading = atom<boolean>(false)

export const sidebarFiltersOpen = atom<boolean>(false)
